/**
 * 신호등·할일 도메인 타입 — 52-Planner 조회 API 응답 그대로(신호_이후_구현지시서 4장).
 * GET /schedule/signals · GET /schedule/todos · GET /schedule/sites/signals
 * 날짜(LocalDate)는 "YYYY-MM-DD", 일시(LocalDateTime)는 "YYYY-MM-DDTHH:mm:ss" 문자열.
 * 신호(SIG-01)·할일(TODO-01)은 서버가 상태명·사유 문장·할일 제목·버튼·이동 주소까지 완성해 내려준다 — 화면은 그대로 표시한다
 * (S14-S15 구현설계서 5.2절). 할일 코드(todoCode)는 화면에 보이지 않으며 화면이 이 값으로 분기하지 않는다.
 */

/** 신호 3줄: 반장 · 발주 · 작업 (공통코드 ZEU_TASK_SIGNAL_SLOT) */
export type SignalSlot = 'FOREMAN' | 'ORDER' | 'WORK';

/** 신호 색 (공통코드 ZEU_SIGNAL_COLOR). NONE = 빗금(해당 없음) */
export type SignalColor = 'WHITE' | 'YELLOW' | 'RED' | 'GREEN' | 'BLUE' | 'NONE';

/** 할일 코드 (TM_SITE_TODO.TODO_CODE, 예 A01). 화면에 표시하지 않는다 */
export type TodoCode = string;

/** 처리 버튼 동작 유형 (공통코드 E01_TODO_ACTION_TYPE) — ROUTE 화면 이동 · API 바로 실행 */
export type TodoActionType = 'ROUTE' | 'API' | (string & {});

/** 처리 버튼 — actionName 이 null 이면 버튼을 그리지 않는다. ROUTE 면 webPath 로 이동, API 면 actionUrl 호출 */
export interface SignalActionFields {
  actionName?: string | null;
  actionTypeCode?: TodoActionType | null;
  webPath?: string | null;
  appPath?: string | null;
  actionUrl?: string | null;
}

/** 신호 사유 코드 (공통코드 ZEU_SIGNAL_REASON) — 반장 12 · 작업 8 · 발주 14 */
export type ForemanReasonCode =
  | 'FOREMAN_UNASSIGNED' | 'FOREMAN_ASSIGNED_AUTO' | 'FOREMAN_ASSIGNED_MANUAL' | 'FOREMAN_UNASSIGNABLE'
  | 'FOREMAN_UNASSIGNED_SOON' | 'FOREMAN_WAITING' | 'FOREMAN_REWAITING' | 'FOREMAN_NEXT_WAITING'
  | 'FOREMAN_CONFIRMED' | 'FOREMAN_DECLINED' | 'FOREMAN_UNFIXED'
  | 'FOREMAN_NA';

export type WorkReasonCode =
  | 'WORK_NOT_REQUESTED' | 'WORK_REQUEST_DUE' | 'WORK_REPORT_WAIT' | 'WORK_REPORT_PROGRESS'
  | 'WORK_REPORT_DELAY' | 'WORK_REPORT_ISSUE' | 'WORK_INSPECT_WAIT' | 'WORK_DONE';

export type OrderReasonCode =
  | 'ORDER_NA' | 'ORDER_BEFORE_DATE' | 'ORDER_SEND_DUE' | 'ORDER_SEND_OVERDUE' | 'ORDER_REPLY_WAIT'
  | 'ORDER_CONFIRMED' | 'ORDER_REASSIGN_NEEDED' | 'ORDER_REREPLY_WAIT' | 'ORDER_RESEND_NEEDED' | 'ORDER_SHIP_INFO_WAIT'
  | 'ORDER_TODAY_DELIVERY' | 'ORDER_RECEIPT_WAIT' | 'ORDER_RECEIVED' | 'ORDER_RECEIPT_DELAY' | 'ORDER_RECEIPT_ISSUE';

export type SignalReasonCode = ForemanReasonCode | WorkReasonCode | OrderReasonCode;

/** 신호 1줄(공종 × 슬롯) — 판독 결과 + 서버가 완성한 문구·할일·버튼 */
export interface SignalItem extends SignalActionFields {
  signalTypeCode: SignalSlot;
  colorCode: SignalColor;
  /** 서버가 새 코드를 추가해도 화면이 깨지지 않도록 string 허용 — signalPresentation 이 모르는 코드는 null 처리 */
  reasonCode: SignalReasonCode | (string & {});
  reasonDate: string | null;
  reasonDtm: string | null;
  todoCode: TodoCode | null;
  prevColorCode: SignalColor | null;
  nextJudgeDtm: string | null;
  /** 상태명 (예: 미확정) */
  reasonName?: string | null;
  /** 한 줄 문구 — 상세창 요약 */
  reasonLine?: string | null;
  /** 팝오버 사유 문장 */
  reasonText?: string | null;
  /** 할일 제목 · 짧은 제목 · 사유 문장 (할일 없으면 null) */
  todoName?: string | null;
  todoAbbrName?: string | null;
  todoReasonText?: string | null;
}

/** 공종 1개의 신호 3줄 + 대표색 */
export interface TaskSignal {
  taskId: number;
  workTypeCode: string;
  /** 공종명 */
  workTypeName?: string | null;
  signals: SignalItem[];
  /** 흰색·빗금 제외 가장 급한 색(빨강→노랑→파랑→녹색). 모두 흰색이면 null */
  representativeColorCode: SignalColor | null;
}

/** GET /schedule/signals?siteId=&taskIds=&colors= 응답 data */
export interface SignalsResponse {
  items: TaskSignal[];
  /** 남은 공정의 빨강 · 노랑 신호 수, 할일이 있는 신호 수 */
  summary?: { redCount: number; yellowCount: number; todoCount: number };
}

/** GET /schedule/sites/signals 응답 data.items[] */
export interface SiteSignal {
  siteId: number;
  representativeColorCode: SignalColor | null;
  redCount: number;
  yellowCount: number;
  topReason: {
    taskId: number;
    workTypeCode: string;
    todoCode: TodoCode | null;
    reasonCode: SignalReasonCode | (string & {});
  } | null;
}

export interface SiteSignalsResponse {
  items: SiteSignal[];
}

/** 할일 줄 level: r = 지금 처리(빨강) · y = 오늘 일정(노랑) */
export type TodoLevel = 'r' | 'y';

/** 보조 동작 — 반장님 전화 (웹에서는 비활성, 안내 말풍선에 이름 · 번호 표시) */
export interface TodoMoreAction {
  label: string;
  /** 전화 걸 번호(원문) — 앱이 바로 전화할 때 쓴다 */
  tel: string;
  /** 반장님 이름(없으면 null) */
  name?: string | null;
  /** 하이픈 넣은 번호 표기(예: 010-1234-5678) */
  telText?: string | null;
}

/** GET /schedule/todos?scope=today|week 응답 data.items[] — 서버가 정한 순서 그대로 */
export interface TodoItem extends SignalActionFields {
  todoCode: TodoCode;
  level: TodoLevel;
  siteId: number;
  siteName: string | null;
  taskId: number;
  workTypeCode: string;
  signalTypeCode: SignalSlot;
  reasonCode: SignalReasonCode | (string & {});
  reasonDate: string | null;
  reasonDtm: string | null;
  /** 해당 현장 CHARGE·MANAGER 회원 ID */
  ownerMemberIds: number[];
  /** 공종명 */
  workTypeName: string | null;
  /** 할일 제목 · 짧은 제목 · 사유 문장 */
  todoName: string | null;
  todoAbbrName: string | null;
  todoReasonText: string | null;
  /** 줄(버튼 밖)을 누르면 여는 자세히 보기 주소. null 이면 줄을 누를 수 없다 */
  viewPath: string | null;
  /** 보조 동작(반장님 전화) — 없으면 빈 배열 */
  moreActions: TodoMoreAction[];
  /** 오늘 알림 시각 이후 생긴 할일 */
  isNew: boolean;
  changedDtm: string | null;
}

/** 7일 일정 — 입고 · 착공 · 공사완료 */
export type WeekEventType = 'DELIVERY' | 'START' | 'END';

export interface WeekEvent {
  type: WeekEventType | (string & {});
  taskId: number;
  workTypeName: string | null;
}

export interface WeekDay {
  /** 'YYYY-MM-DD' */
  date: string;
  sites: { siteId: number; siteName: string | null; events: WeekEvent[] }[];
}

export interface TodosResponse {
  items: TodoItem[];
  /** 지금 처리할 일 · 오늘 일정 수, 7일 일정 수(scope=week 일 때) */
  summary: { pendingCount: number; todayCount: number; weekCount?: number };
  /** scope=week 일 때만 — 오늘~6일 뒤 날짜별 · 현장별 일정 */
  weekDays?: WeekDay[];
}

/** 서버 신호 누락 오류 코드 (HTTP 503) — data.missingTaskIds 와 함께 내려온다. 폴백으로 숨기지 않는다. */
export const SIGNAL_MISSING_CODE = 'SIGNAL_MISSING';
